import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { Button, Table, Drawer, Tabs, Input, Tag, Space, Modal } from "antd";
import ToggleSwitch from "src/components/ui/switch";
import { EyeIcon } from "src/components/icons/eye-icon";
import { debounce } from "lodash";

type ServiceAlertRecord = {
  id: number;
  service_name: string;
  email: boolean;
  sms: boolean;
  slack: boolean;
  email_recipients?: string;
  sms_recipients?: string;
  slack_recipients?: string;
  updatedAt: string;
};

type RecipientField =
  | "email_recipients"
  | "sms_recipients"
  | "slack_recipients";

interface RecipientEditorProps {
  record: ServiceAlertRecord;
  field: RecipientField;
  onSave?: (field: RecipientField, values: string[]) => void;
  canEdit?: boolean;
}

const ServiceAlerts = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList, canEdit } = usePermissions(
    permissionConstants.SERVICE_ALERTS
  );
  const canViewAlerts = canView || canList;

  const [dataSource, setDataSource] = useState<ServiceAlertRecord[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedRecord, setSelectedRecord] =
    useState<ServiceAlertRecord | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /** ---------------- Fetch Templates API ---------------- */
  const { mutate: getTemplatesList, isLoading } = useMutation(
    (params: { page: number; limit: number; q?: string }) =>
      client.serviceAlerts.getAllServicesList(params),

    {
      onSuccess: (data) => {
        setDataSource(
          data.items.map((item: any) => ({
            ...item,
          })),
        );
        setTotalItems(data.totalcount || 0);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateTemplate, isLoading: isUpdating } = useMutation(
    client.serviceAlerts.updateService,
    {
      onSuccess: (data) => {
        setAlertText(data.message);
        setIsAlertOpened(true);
        getTemplatesList({ page: currentPage, limit: pageSize });
      },
      onError: (error: any) => {
        setAlertText(
          error.response
            ? getErrorMessage(error.response.data)
            : APISomethingWrongMsg,
        );
        setIsAlertOpened(true);
      },
    },
  );

  const handleToggleChange = (
    record: ServiceAlertRecord,
    field: "email" | "sms" | "slack",
    newValue: boolean,
  ) => {
    if (!canEdit) {
      setAlertText("You do not have permission to update alert settings");
      setIsAlertOpened(true);
      return;
    }
    setSelectedRecord(record);

    updateTemplate({
      id: record.id,
      service_name: record.service_name,
      [field]: newValue, // dynamic field update
    });
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCurrentPage(1); // reset page when searching
        setSearchText(value.trim());
      }, 500),
    [],
  );
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isMounted || !canViewAlerts) return;

    getTemplatesList({
      page: currentPage,
      limit: pageSize,
      q: searchText || undefined,
    });
  }, [isMounted, canViewAlerts, currentPage, pageSize, searchText]);

  const columns: ColumnsType<any> = [
    { title: "Service Name", dataIndex: "service_name", key: "service_name" },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => {
        const isDisabled = !canEdit;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}-email`}
              value={!!email}
              disabled={isDisabled}
              onValueChanged={(_: any, newValue: boolean) =>
                handleToggleChange(record, "email", newValue)
              }
            />
          </div>
        );
      },
    },
    {
      title: "SMS",
      dataIndex: "sms",
      key: "sms",
      render: (sms, record) => {
        const isDisabled = !canEdit;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}-sms`}
              value={!!sms}
              disabled={isDisabled}
              onValueChanged={(_: any, newValue: boolean) =>
                handleToggleChange(record, "sms", newValue)
              }
            />
          </div>
        );
      },
    },
    // {
    //   title: "Slack",
    //   dataIndex: "slack",
    //   key: "slack",
    //   render: (slack, record) => (
    //     <ToggleSwitch
    //       type={`${record.id}-slack`}
    //       value={!!slack}
    //       onValueChanged={(_: any, newValue: boolean) =>
    //         handleToggleChange(record, "slack", newValue)
    //       }
    //     />
    //   ),
    // },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
    {
      title: "Recipients",
      key: "view",
      render: (_, record) => (
        <a
          onClick={() => {
            setSelectedRecord(record);
            setIsDrawerOpen(true);
          }}
          className="text-secondary hover:text-primary cursor-pointer"
        >
          <EyeIcon className="w-4 h-4" />
        </a>
      ),
    },
  ];

  return (
    <div className="w-full h-full bg-gray">
      <>
        <div className="flex justify-between items-center p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium">
            Alert Services
          </p>
          <input
            type="text"
            placeholder="Search by service name"
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value); // update UI instantly
              debouncedSearch(value); // call API after delay
            }}
            className="border px-3 py-2 rounded-md w-[250px] focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {!canViewAlerts ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view alert services.
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            <Table
              loading={isLoading}
              showSorterTooltip={false}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                total: totalItems,
                current: currentPage,
                onChange: (page, size) => setCurrentPage(page),
                position: ["bottomCenter"],
                showSizeChanger: false,
              }}
            />
          </div>
        )}
      </>
      <Drawer
        title={`Recipients - ${selectedRecord?.service_name}`}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        width={500}
        // closable
        // closeIcon={<span className="text-lg font-bold">✕</span>}
        // extra={<Button onClick={() => setIsDrawerOpen(false)}>Close</Button>}
      >
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h3 className="text-lg font-semibold">
            {selectedRecord?.service_name}
          </h3>

          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-500 hover:text-black text-xl font-bold"
          >
            ✕
          </button>
        </div>
        {selectedRecord && (
          <Tabs
            items={[
              {
                key: "email",
                label: "Email",
                children: (
                  <RecipientEditor
                    field="email_recipients"
                    record={selectedRecord}
                    canEdit={canEdit}
                    onSave={(field, values) => {
                      updateTemplate({
                        id: selectedRecord.id,
                        service_name: selectedRecord.service_name,
                        [field]: JSON.stringify(values),
                      });
                    }}
                  />
                ),
              },
              {
                key: "sms",
                label: "SMS",
                children: (
                  <RecipientEditor
                    field="sms_recipients"
                    record={selectedRecord}
                    canEdit={canEdit}
                    onSave={(field, values) => {
                      updateTemplate({
                        id: selectedRecord.id,
                        service_name: selectedRecord.service_name,
                        [field]: JSON.stringify(values),
                      });
                    }}
                  />
                ),
              },
              // {
              //   key: "slack",
              //   label: "Slack",
              //   children: (
              //     <RecipientEditor
              //       field="slack_recipients"
              //       record={selectedRecord}
              //       onSave={(field, values) => {
              //         updateTemplate({
              //           id: selectedRecord.id,
              //           service_name: selectedRecord.service_name,
              //           [field]: JSON.stringify(values),
              //         });
              //       }}
              //     />
              //   ),
              // },
            ]}
          />
        )}
      </Drawer>
    </div>
  );
};

export default ServiceAlerts;

const parseRecipients = (value?: string): string[] => {
  try {
    return JSON.parse(value || "[]");
  } catch {
    return [];
  }
};

const RecipientEditor: React.FC<RecipientEditorProps> = ({
  record,
  field,
  onSave,
  canEdit = true,
}) => {
  const [items, setItems] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setItems(parseRecipients(record[field]));
    setError("");
  }, [record, field]);

  /* ---------------- Validators ---------------- */

  const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value);

  const isValidPhone = (value: string) => /^\+\d{6,15}$/.test(value);
  const isValidSlack = (value: string) => /^[A-Za-z0-9_-]{6,}$/.test(value);

  const isValidValue = (value: string) => {
    if (field === "email_recipients") return isValidEmail(value);
    if (field === "sms_recipients") return isValidPhone(value);
    if (field === "slack_recipients") return isValidSlack(value);
    return true;
  };

  /* ---------------- Add Items ---------------- */

  const processInput = () => {
    if (!canEdit) return;
    if (input.trim()) {
      const values = input
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => {
          if (field === "sms_recipients") {
            // Auto add + if missing
            if (!v.startsWith("+")) {
              return `+${v}`;
            }
          }
          return v;
        });

      const invalidValues = values.filter((v) => !isValidValue(v));
      const validValues = values.filter(isValidValue);
      const uniqueValues = validValues.filter((v) => !items.includes(v));

      if (invalidValues.length > 0) {
        setError(`Invalid value(s): ${invalidValues.join(", ")}`);
        return;
      }

      if (uniqueValues.length === 0) {
        setError("Value already added or empty");
        return;
      }

      const updatedItems = [...items, ...uniqueValues];
      setItems(updatedItems);
      setInput("");
      setError("");

      // auto save after adding
      onSave?.(field, updatedItems);
      return;
    }

    setError("");
    onSave?.(field, items);
  };

  const removeItem = (value: string) => {
    if (!canEdit) return;
    setItems((prev) => prev.filter((i) => i !== value));
  };

  /* ---------------- UI Helpers ---------------- */

  const getPlaceholder = (field: RecipientField) => {
    if (field === "email_recipients")
      return "Add email (space/comma separated)";
    if (field === "sms_recipients")
      return "Add phone number with country code (6–15 digits)";
    return "Add Slack ID";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex justify-between items-center mb-2">
          <div className="font-medium text-sm">Recipients</div>

          {canEdit && items.length > 1 && (
            <button
              className="text-red-500 hover:text-red-600 text-sm"
              onClick={() => {
                Modal.confirm({
                  title: "Remove all recipients?",
                  content: "This action cannot be undone.",
                  okText: "Yes",
                  cancelText: "Cancel",
                  okType: "danger",
                  onOk() {
                    setItems([]);
                    onSave?.(field, []);
                  },
                });
              }}
            >
              Clear All
            </button>
          )}
        </div>

        <Space wrap>
          {items.map((item) => (
            <Tag key={item} closable={canEdit} onClose={() => removeItem(item)}>
              {item}
            </Tag>
          ))}
        </Space>

        {canEdit && (
          <div style={{ marginTop: 12 }}>
            <Input
              status={error ? "error" : ""}
              placeholder={getPlaceholder(field)}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setError(""); // clear error while typing
              }}
              onPressEnter={processInput}
            />

            {/* Error message */}
            {error && (
              <div
                style={{
                  color: "#ff4d4f",
                  marginTop: 6,
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky Save */}
      {canEdit && (
        <Button
          type="primary"
          className="!bg-blue-600 !text-white !border-blue-600 mt-3"
          onClick={processInput}
        >
          Save
        </Button>
      )}
    </div>
  );
};
